export type ChatFormatType = 'bold' | 'italic' | 'strikethrough' | 'code' | 'monospace' | 'numbered-list' | 'bulleted-list' | 'quote';

export interface IFormatWrapMarker {
  prefix: string;
  suffix: string;
}

export interface ITextEditResult {
  nextValue: string;
  nextCaretStart: number;
  nextCaretEnd: number;
}

const NOT_FOUND_INDEX = -1;
const PAIR_STEP = 2;
const MONOSPACE_TOKEN = '```';

export const CHAT_WRAP_MARKERS: Partial<Record<ChatFormatType, IFormatWrapMarker>> = {
  bold: { prefix: '*', suffix: '*' },
  italic: { prefix: '_', suffix: '_' },
  strikethrough: { prefix: '~', suffix: '~' },
  code: { prefix: '`', suffix: '`' },
  monospace: { prefix: MONOSPACE_TOKEN, suffix: MONOSPACE_TOKEN },
};

export const CHAT_LINE_PREFIX_MARKERS: Partial<Record<ChatFormatType, string>> = {
  quote: '> ',
  'bulleted-list': '- ',
};

// Mirrors chat-format.pipe.ts's line-classification grammar (ORDERED_LIST_LINE / UNORDERED_LIST_LINE /
// QUOTE_LINE) so the toolbar's "is this line already formatted" detection agrees with how the message
// actually renders once sent - e.g. both accept '-' or '*' as a bullet, and a quote with or without a
// trailing space after '>'.
const NUMBERED_LIST_LINE = /^(\d+)\.\s+/;
const BULLETED_LIST_LINE = /^[-*]\s+/;
const QUOTE_LINE = /^>\s?/;

function matchLinePrefix(type: ChatFormatType, line: string): RegExpExecArray | null {
  if (type === 'bulleted-list') {
    return BULLETED_LIST_LINE.exec(line);
  }
  if (type === 'quote') {
    return QUOTE_LINE.exec(line);
  }
  return null;
}

// Single source for "which line is the caret on and what does it span" - toggleLinePrefixAtCaret,
// isLineFormatActive, and continueListOnEnter all need this and previously each recomputed it.
function getLineBounds(value: string, caret: number): { lineStart: number; lineEnd: number; line: string } {
  const lineStart = value.lastIndexOf('\n', Math.max(caret - 1, 0)) + 1;
  const lineEndIndex = value.indexOf('\n', caret);
  const lineEnd = lineEndIndex === NOT_FOUND_INDEX ? value.length : lineEndIndex;
  return { lineStart, lineEnd, line: value.slice(lineStart, lineEnd) };
}

function buildLinePrefixedText(type: ChatFormatType, selectedText: string): string {
  const lines = selectedText.length > 0 ? selectedText.split('\n') : [''];
  if (type === 'numbered-list') {
    return lines.map((line, index) => `${index + 1}. ${line}`).join('\n');
  }

  const prefix = CHAT_LINE_PREFIX_MARKERS[type] ?? '';
  return lines.map((line) => `${prefix}${line}`).join('\n');
}

function isOddCount(count: number): boolean {
  return count % PAIR_STEP === 1;
}

function isLineWrapped(line: string, marker: IFormatWrapMarker): boolean {
  return line.length >= marker.prefix.length + marker.suffix.length && line.startsWith(marker.prefix) && line.endsWith(marker.suffix);
}

function isMultilineSelectionWrapped(selectionText: string, marker: IFormatWrapMarker): boolean {
  const lines = selectionText.split('\n').filter((line) => line.length > 0);
  return lines.length > 0 && lines.every((line) => isLineWrapped(line, marker));
}

function unwrapLine(line: string, marker: IFormatWrapMarker): string {
  return line.length > 0 && isLineWrapped(line, marker) ? line.slice(marker.prefix.length, line.length - marker.suffix.length) : line;
}

function unwrapMultilineSelection(selectionText: string, marker: IFormatWrapMarker): string {
  return selectionText
    .split('\n')
    .map((line) => unwrapLine(line, marker))
    .join('\n');
}

const WORD_CHAR_PATTERN = /[\p{L}\p{N}]/u;

function isWordChar(char: string | undefined): boolean {
  return !!char && WORD_CHAR_PATTERN.test(char);
}

// ChatFormatPipe's word-boundary rule rejects a marker glued to a letter/digit (so "5*3=15" stays plain) -
// a selection that starts or ends mid-word would otherwise produce a marker the renderer can never
// recognize (e.g. selecting "ell" in "hello" and bolding it would insert "h*ell*o"). Grow the selection
// outward to the nearest non-word boundary so the inserted markers always land somewhere valid.
function expandToWordBoundary(value: string, start: number, end: number): { start: number; end: number } {
  let expandedStart = start;
  let expandedEnd = end;
  while (expandedStart > 0 && isWordChar(value[expandedStart - 1])) {
    expandedStart--;
  }
  while (expandedEnd < value.length && isWordChar(value[expandedEnd])) {
    expandedEnd++;
  }
  return { start: expandedStart, end: expandedEnd };
}

function trimSelectionWhitespace(value: string, start: number, end: number): { start: number; end: number } {
  let trimmedStart = start;
  let trimmedEnd = end;
  while (trimmedStart < trimmedEnd && /\s/.test(value[trimmedStart])) {
    trimmedStart++;
  }
  while (trimmedEnd > trimmedStart && /\s/.test(value[trimmedEnd - 1])) {
    trimmedEnd--;
  }
  return { start: trimmedStart, end: trimmedEnd };
}

// A ``` monospace fence is three consecutive backtick characters; naively pairing single backticks
// (token === '`') would see each fence as 3 separate occurrences and pair across fence boundaries,
// corrupting the fence when toggling "code" at a caret inside it. Collect the ranges each monospace
// fence occupies so single-backtick collection can skip over them entirely.
function findMonospaceFenceRanges(value: string): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = [];
  const occurrences: number[] = [];
  let idx = value.indexOf(MONOSPACE_TOKEN);
  while (idx !== NOT_FOUND_INDEX) {
    occurrences.push(idx);
    idx = value.indexOf(MONOSPACE_TOKEN, idx + MONOSPACE_TOKEN.length);
  }
  for (let i = 0; i + 1 < occurrences.length; i += PAIR_STEP) {
    ranges.push({ start: occurrences[i], end: occurrences[i + 1] + MONOSPACE_TOKEN.length });
  }
  return ranges;
}

function findWrapSpan(value: string, position: number, token: string): { start: number; end: number; closed: boolean } | null {
  const monospaceRanges = token === '`' ? findMonospaceFenceRanges(value) : [];
  const occurrences: number[] = [];
  let idx = value.indexOf(token);
  while (idx !== NOT_FOUND_INDEX) {
    if (!monospaceRanges.some((range) => idx >= range.start && idx < range.end)) {
      occurrences.push(idx);
    }
    idx = value.indexOf(token, idx + token.length);
  }

  for (let i = 0; i + 1 < occurrences.length; i += PAIR_STEP) {
    const openIdx = occurrences[i];
    const closeIdx = occurrences[i + 1];
    if (openIdx < position && position <= closeIdx) {
      return { start: openIdx, end: closeIdx, closed: true };
    }
  }

  if (isOddCount(occurrences.length)) {
    const lastOpenIdx = occurrences[occurrences.length - 1];
    if (position > lastOpenIdx) {
      return { start: lastOpenIdx, end: value.length, closed: false };
    }
  }

  return null;
}

export function wrapSelection(value: string, start: number, end: number, marker: IFormatWrapMarker): ITextEditResult {
  const trimmed = trimSelectionWhitespace(value, start, end);
  if (trimmed.start === trimmed.end) {
    return { nextValue: value, nextCaretStart: start, nextCaretEnd: end };
  }
  const { start: expandedStart, end: expandedEnd } = expandToWordBoundary(value, trimmed.start, trimmed.end);
  const selectedText = value.slice(expandedStart, expandedEnd);

  // Wrap-style markers can't span a line break - ChatFormatPipe parses each line independently, so a
  // single marker pair wrapped around multiple lines (e.g. "*foo\nbar*") would never render as formatted
  // on either line. Wrap each non-empty line of the selection separately instead.
  const formattedText = selectedText.includes('\n')
    ? selectedText
        .split('\n')
        .map((line) => (line.length > 0 ? `${marker.prefix}${line}${marker.suffix}` : line))
        .join('\n')
    : `${marker.prefix}${selectedText}${marker.suffix}`;

  return {
    nextValue: `${value.slice(0, expandedStart)}${formattedText}${value.slice(expandedEnd)}`,
    nextCaretStart: selectedText.includes('\n') ? expandedStart : expandedStart + marker.prefix.length,
    nextCaretEnd: selectedText.includes('\n')
      ? expandedStart + formattedText.length
      : expandedStart + marker.prefix.length + selectedText.length,
  };
}

export function toggleWrapOnSelection(value: string, start: number, end: number, marker: IFormatWrapMarker): ITextEditResult {
  const { lineStart, lineEnd, line } = getLineBounds(value, start);
  if (end > lineEnd) {
    const selectionText = value.slice(start, end);
    if (isMultilineSelectionWrapped(selectionText, marker)) {
      const unwrappedText = unwrapMultilineSelection(selectionText, marker);
      return {
        nextValue: `${value.slice(0, start)}${unwrappedText}${value.slice(end)}`,
        nextCaretStart: start,
        nextCaretEnd: start + unwrappedText.length,
      };
    }
    return wrapSelection(value, start, end, marker);
  }

  const probeInLine = Math.min(start - lineStart + marker.prefix.length, line.length);
  const span = findWrapSpan(line, probeInLine, marker.prefix);
  if (!span?.closed) {
    return wrapSelection(value, start, end, marker);
  }

  const spanStart = lineStart + span.start;
  const spanEnd = lineStart + span.end;
  const spanCloseEnd = spanEnd + marker.suffix.length;
  if (start < spanStart || end > spanCloseEnd) {
    return wrapSelection(value, start, end, marker);
  }

  const contentStart = spanStart + marker.prefix.length;
  const contentEnd = spanEnd;
  return {
    nextValue: `${value.slice(0, spanStart)}${value.slice(contentStart, contentEnd)}${value.slice(spanCloseEnd)}`,
    nextCaretStart: spanStart,
    nextCaretEnd: spanStart + (contentEnd - contentStart),
  };
}

export function toggleWrapAtCaret(value: string, caret: number, marker: IFormatWrapMarker): ITextEditResult {
  // A wrap-style marker can't span a line break (ChatFormatPipe parses each line independently), so the
  // span search is scoped to the caret's own line - an open/close pair on another line is irrelevant here.
  const { lineStart, line } = getLineBounds(value, caret);
  const caretInLine = caret - lineStart;
  const span = findWrapSpan(line, caretInLine, marker.prefix);

  if (!span) {
    const caretPosition = caret + marker.prefix.length;
    return {
      nextValue: `${value.slice(0, caret)}${marker.prefix}${marker.suffix}${value.slice(caret)}`,
      nextCaretStart: caretPosition,
      nextCaretEnd: caretPosition,
    };
  }

  if (!span.closed) {
    const caretPosition = caret + marker.suffix.length;
    return {
      nextValue: `${value.slice(0, caret)}${marker.suffix}${value.slice(caret)}`,
      nextCaretStart: caretPosition,
      nextCaretEnd: caretPosition,
    };
  }

  const absoluteEnd = lineStart + span.end;
  const caretPosition = caret + marker.suffix.length;
  return {
    nextValue: `${value.slice(0, caret)}${marker.suffix}${value.slice(caret, absoluteEnd)}${value.slice(absoluteEnd + marker.suffix.length)}`,
    nextCaretStart: caretPosition,
    nextCaretEnd: caretPosition,
  };
}

export function prefixSelectionLines(value: string, start: number, end: number, type: ChatFormatType): ITextEditResult {
  const formattedText = buildLinePrefixedText(type, value.slice(start, end));
  return {
    nextValue: `${value.slice(0, start)}${formattedText}${value.slice(end)}`,
    nextCaretStart: start,
    nextCaretEnd: start + formattedText.length,
  };
}

export function toggleLinePrefixAtCaret(value: string, caret: number, type: ChatFormatType): ITextEditResult {
  const { lineStart, lineEnd, line } = getLineBounds(value, caret);
  const offsetInLine = caret - lineStart;

  const numberedMatch = NUMBERED_LIST_LINE.exec(line);
  const linePrefix = CHAT_LINE_PREFIX_MARKERS[type];
  const prefixMatch = matchLinePrefix(type, line);

  let newLine: string;
  let offsetDelta: number;

  if (type === 'numbered-list') {
    if (numberedMatch) {
      newLine = line.slice(numberedMatch[0].length);
      offsetDelta = -numberedMatch[0].length;
    } else {
      const marker = '1. ';
      newLine = `${marker}${line}`;
      offsetDelta = marker.length;
    }
  } else if (prefixMatch) {
    newLine = line.slice(prefixMatch[0].length);
    offsetDelta = -prefixMatch[0].length;
  } else {
    newLine = `${linePrefix ?? ''}${line}`;
    offsetDelta = (linePrefix ?? '').length;
  }

  const nextCaret = lineStart + Math.max(0, offsetInLine + offsetDelta);
  return {
    nextValue: `${value.slice(0, lineStart)}${newLine}${value.slice(lineEnd)}`,
    nextCaretStart: nextCaret,
    nextCaretEnd: nextCaret,
  };
}

export function isWrapTokenActive(value: string, start: number, end: number, type: ChatFormatType): boolean {
  const marker = CHAT_WRAP_MARKERS[type];
  if (!marker) {
    return false;
  }
  const { lineStart, lineEnd, line } = getLineBounds(value, start);
  if (end > lineEnd) {
    // Multiline wrapping is applied per line, so a multiline selection is active only when each non-empty
    // selected line is wrapped with the same marker pair.
    return isMultilineSelectionWrapped(value.slice(start, end), marker);
  }
  const span = findWrapSpan(line, start - lineStart, marker.prefix);
  return !!span && end - lineStart <= span.end;
}

// Inline code (`) and monospace (```) share the backtick character - toggling one while the caret sits
// inside an already-closed span of the other would corrupt the shared fence, so callers should skip the
// toggle entirely in that case rather than needing to know the two types share a character.
const CONFLICTING_WRAP_TYPES: Partial<Record<ChatFormatType, ChatFormatType>> = {
  code: 'monospace',
  monospace: 'code',
};

export function isWrapTypeBlockedByConflict(value: string, start: number, end: number, type: ChatFormatType): boolean {
  const conflictingType = CONFLICTING_WRAP_TYPES[type];
  return !!conflictingType && isWrapTokenActive(value, start, end, conflictingType);
}

export function isLineFormatActive(value: string, caret: number, type: ChatFormatType): boolean {
  const { line } = getLineBounds(value, caret);

  if (type === 'numbered-list') {
    return NUMBERED_LIST_LINE.test(line);
  }

  return !!matchLinePrefix(type, line);
}

// Renumbers a contiguous run of numbered-list lines immediately following a newline-inserted item, so
// pressing Enter in the middle of an existing numbered list doesn't leave a duplicate ordinal behind
// (e.g. inserting a new "2." between "1." and "2." must bump the old "2." to "3.", and so on).
function renumberFollowingNumberedLines(tailFromLineBreak: string, startNumber: number): string {
  const firstBreakIndex = tailFromLineBreak.indexOf('\n');
  if (firstBreakIndex === NOT_FOUND_INDEX) {
    return tailFromLineBreak;
  }

  const head = tailFromLineBreak.slice(0, firstBreakIndex + 1);
  const lines = tailFromLineBreak.slice(firstBreakIndex + 1).split('\n');
  let nextNumber = startNumber;

  for (let i = 0; i < lines.length; i++) {
    const match = NUMBERED_LIST_LINE.exec(lines[i]);
    if (!match) {
      break;
    }
    lines[i] = `${nextNumber}. ${lines[i].slice(match[0].length)}`;
    nextNumber += 1;
  }

  return head + lines.join('\n');
}

export function continueListOnEnter(
  value: string,
  selectionStart: number,
  selectionEnd: number,
): { nextValue: string; nextCaret: number } | null {
  const workingValue = `${value.slice(0, selectionStart)}${value.slice(selectionEnd)}`;
  const caret = selectionStart;

  const { lineStart, lineEnd, line } = getLineBounds(workingValue, caret);

  const numberedMatch = NUMBERED_LIST_LINE.exec(line);
  const bulletedMatch = !numberedMatch ? BULLETED_LIST_LINE.exec(line) : null;

  if (!numberedMatch && !bulletedMatch) {
    return null;
  }

  const markerLength = numberedMatch ? numberedMatch[0].length : (bulletedMatch as RegExpExecArray)[0].length;
  const hasContentAfterMarker = line.slice(markerLength).trim().length > 0;

  if (!hasContentAfterMarker) {
    return {
      nextValue: `${workingValue.slice(0, lineStart)}${workingValue.slice(lineEnd)}`,
      nextCaret: lineStart,
    };
  }

  // A caret positioned inside or before the marker (e.g. at the very start of "- item") would otherwise
  // split the line in the wrong place and duplicate the marker (e.g. "\n- - item"). Clamp the split point
  // to the start of the item's content so Enter always inserts the new marker between two valid items.
  const splitCaret = Math.max(caret, lineStart + markerLength);

  let newMarker: string;
  let tail: string;
  if (numberedMatch) {
    const nextNumber = parseInt(numberedMatch[1], 10) + 1;
    newMarker = `${nextNumber}. `;
    tail = renumberFollowingNumberedLines(workingValue.slice(splitCaret), nextNumber + 1);
  } else {
    newMarker = (bulletedMatch as RegExpExecArray)[0];
    tail = workingValue.slice(splitCaret);
  }
  const insertion = `\n${newMarker}`;

  return {
    nextValue: `${workingValue.slice(0, splitCaret)}${insertion}${tail}`,
    nextCaret: splitCaret + insertion.length,
  };
}
